import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Setting } from '@app-state';
import { IModalResult } from '@components';
import {
    enumMapper,
    FeatureFlag,
    FolderType,
    IRobot,
    IRobotLicenseUpdate,
    ISession,
    ISessionUpdate,
    LicenseErrorCode,
    ROBOT_LICENSE_TYPE_FILTER,
    ROBOT_STATE_FILTER,
    RobotHostingType,
    RobotLicenseType,
    RobotProvisionType,
    RobotState,
    RobotType,
    RobotTypeAttended,
} from '@models';
import { FILTER_BUILDER } from '@pages/generics/filter-builder';
import {
    GRID_SERVICE,
    ODataGridComponent,
    STATE_HANDLER,
    urlStateHandler,
} from '@pages/generics/grid';
import { RobotFilterBuilder } from '@pages/robots/filter-builder/robot-filter-builder';
import { FloatingRobotModalComponent } from '@pages/robots/modal/robot-floating.modal';
import { StandardRobotModalComponent } from '@pages/robots/modal/robot-standard.modal';
import { CheckPermissionsModalComponent } from '@pages/users/modals';
import {
    EnvironmentService,
    MachineService,
    ODataQueryOptions,
    RobotService,
    SessionsService,
    SignalRService,
    UserService,
} from '@services';
import { filterNullOrUndefined } from '@utils/rxjs.utils';

import * as moment from 'moment';
import {
    BehaviorSubject,
    merge,
    Subscription,
} from 'rxjs';
import {
    filter,
    finalize,
    map,
    switchMap,
    takeUntil,
} from 'rxjs/operators';

export class FolderDependentSessionService extends SessionsService {
    public folderType = FolderType.Classic;

    public get(queryOptions?: ODataQueryOptions) {
        const endpoint = this.folderType === FolderType.Classic
            ? '/odata/Sessions'
            : '/odata/Sessions/UiPath.Server.Configuration.OData.GetGlobalSessions';

        return super.get(queryOptions, endpoint);
    }

}

@Component({
    templateUrl: 'robots.page.html',
    styleUrls: ['robots.page.scss'],
    providers: [
        MachineService,
        EnvironmentService,
        UserService,
        {
            provide: GRID_SERVICE,
            useClass: FolderDependentSessionService,
        },
        {
            provide: FILTER_BUILDER,
            useClass: RobotFilterBuilder,
        },
        {
            provide: STATE_HANDLER,
            useValue: urlStateHandler,
        },
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class RobotsPageComponent
    extends ODataGridComponent<ISession, number>
    implements OnInit, OnDestroy, AfterViewInit {


    public get hasBusyRobotsSelected(): boolean {
        return this._grid.selectionManager.selected.some(
            (session: ISession) => session.state === RobotState[RobotState.Busy],
        );
    }

    public isLicenseInvalid$ = this._appState.user.license.value$
        .pipe(
            map(license => license!.isExpired),
        );

    public readonly RobotHostingType = RobotHostingType;
    public readonly RobotType = RobotType;
    public readonly LicenseErrorCode = LicenseErrorCode;
    public readonly RobotProvisionType = RobotProvisionType;
    public readonly userIdLoading$ = new BehaviorSubject<number | null>(null);
    public isGlobalView!: boolean;

    public isMonitoringEnabled = this._appState.settings.isFeatureEnabled(FeatureFlag.Monitoring);
    public isModernFoldersEnabled = !!this._appState.settings.get(Setting.isModernFolderSystemEnabled);
    public isRobotsViewGranted$ = this._appState.user.permissions.isGranted('Robots.View');
    public isRobotsCreateGranted$ = this._appState.user.permissions.isGranted('Robots.Create');
    public isEnvironmentsViewGranted$ = this._appState.user.permissions.isGranted('Environments.View');
    public isMachinesViewGranted$ = this._appState.user.permissions.isGranted('Machines.View');

    public refetchMap = {
        machineName: false,
        userName: false,
    };

    public readonly typeList = enumMapper(RobotType, {
        resourceKey: 'RobotType',
    });

    public readonly hostingTypeList = enumMapper(RobotHostingType, {
        resourceKey: 'RobotHostingType',
    });

    public readonly licenseList = enumMapper(RobotLicenseType, {
        resourceKey: 'RobotLicenseType',
        filterValueMap: ROBOT_LICENSE_TYPE_FILTER,
    });

    public readonly stateList = enumMapper(RobotState, {
        resourceKey: 'RobotState',
        filterValueMap: ROBOT_STATE_FILTER,
    });

    private _environmentService = this._injector.get<EnvironmentService>(EnvironmentService);
    private _robotService = this._injector.get<RobotService>(RobotService);
    private _signalR = this._injector.get<SignalRService>(SignalRService);
    private _user = this._injector.get<UserService>(UserService);
    private _route = this._injector.get<ActivatedRoute>(ActivatedRoute);

    private _modalSubscription?: Subscription;

    ngOnInit() {
        this.isGlobalView = !!this._route.snapshot.data && this._route.snapshot.data.isModernContext;
        (this._service as FolderDependentSessionService).folderType = this.isGlobalView
            ? FolderType.Modern
            : FolderType.Classic;

        this._signalR.sessions$
            .pipe(
                filter(() => !!this._grid.dataManager.length),
                takeUntil(this._destroyed$),
            )
            .subscribe(this._updateSessionState);

        this.data$
            .subscribe(sessions => this._signalR.watchRobots(sessions.map(session => session.robot.id)));

        this._signalR.robotLicenses$
            .pipe(takeUntil(this._destroyed$))
            .subscribe(this._updateRobotLicensesState);

        this._signalR.watchNewRobots();
        this._expand$.next(['Robot($expand=License)']);
    }

    ngOnDestroy() {
        super.ngOnDestroy();
        this._signalR.watchRobots(false);
        this._signalR.watchNewRobots(false);
    }

    public machineNameSuggest = (term: string, size: number, skip: number) => {
        this.refetchMap.machineName = false;
        return this._injector.get<MachineService>(MachineService).machineNameSuggest(term, size, skip);
    }

    public userNameSuggest = (term: string, size: number, skip: number) => {
        this.refetchMap.userName = false;

        const service = this.isGlobalView
            ? (this._service as SessionsService)
            : this._robotService;

        return service.userNameSuggest(term, size, skip);
    }

    public environmentNameSuggest = (term: string, size: number, skip: number) =>
        this._environmentService.environmentNameSuggest(term, size, skip)

    public isFrontOffice = (type: RobotType) => RobotTypeAttended[type] != null;

    public isConvertibleToFloating = (robot: IRobot) => this.isFrontOffice(robot.type) && robot.hostingType === RobotHostingType.Standard;

    public onRemoveItems(items?: Array<ISession>) {
        const toDelete = items || this._grid.selectionManager.selected;

        this._modal.onConfirm(
            null,
            () => {
                this.loading$.next(true);
                const robotIds = toDelete.map(session => session.robot.id);
                const [robotId] = robotIds;
                const deleteAction = toDelete.length === 1 ?
                    this._robotService.delete(robotId) :
                    this._robotService.deleteBulk(robotIds);

                deleteAction
                    .pipe(
                        finalize(() => this.loading$.next(false)),
                    )
                    .subscribe(() => this._refresh$.next());
            },
            () => { },
            this._translate.instant('grid_action_delete_message'),
        );
    }

    public openModal(hostingType: RobotHostingType, session?: ISession, duplicate = false) {

        if (this._modalSubscription) { this._modalSubscription.unsubscribe(); }

        const modalComponent = hostingType === RobotHostingType.Standard ? StandardRobotModalComponent : FloatingRobotModalComponent;

        if (
            session &&
            session.robot
        ) {
            this._modalSubscription = this._robotService
                .getInstance(session.robot.id)
                .pipe(
                    switchMap(robot =>
                        this._modal
                            .openModal(modalComponent, robot, {
                                isDuplicate: duplicate,
                                data: {
                                    model: session,
                                },
                                autoFocus: false,
                            })
                            .afterClosedResult(),
                    ),
                    filter<IModalResult<IRobot>>(Boolean),
                )
                .subscribe(modalResult => {
                    if (!duplicate) {
                        const robot = modalResult.data!;
                        delete robot.license;
                        this._grid.dataManager.patchRow(
                            this._findSessionByRobotId(robot.id)!.id,
                            { robot },
                        );
                    } else {
                        this._refresh$.next();
                    }

                    this._activateRefetch();
                });
        } else {
            const modalRef = this._modal.openModal(
                modalComponent,
                void 0,
                {
                    autoFocus: false,
                });

            this._modalSubscription = merge(
                modalRef.customEvent,
                modalRef.afterClosedResult()
                    .pipe(
                        filterNullOrUndefined(),
                    ),
            ).subscribe(() => {
                this._refresh$.next();
                this._activateRefetch();
            });
        }
    }

    public convertToFloating(robot: IRobot) {
        this._modal.onConfirm(null,
            () => {
                this._robotService.convertToFloating(robot.id)
                    .subscribe(
                        () => this._grid.refresh.next(),
                    );
            },
            () => { },
            this._translate.instant('robot_modal_form_convert_to_floating_info'));
    }

    public formatRobotAcquiredOnDate(date: Date) {
        return moment(date)
            .tz(this._appState.settings.get(Setting.timezone)!)
            .format('L LTS');
    }

    public checkPermissions(userId: number) {
        this.userIdLoading$.next(userId);
        this._user.getInstance(userId).pipe(
            finalize(() => this.userIdLoading$.next(null)),
            switchMap(user =>
                this._modal
                    .openModal(CheckPermissionsModalComponent, user)
                    .afterClosedResult(),
            ),
        ).subscribe();
    }

    protected _ouDependentFilters(): string[] {
        return ['robot.userName', 'robot.environments.id'];
    }

    private _activateRefetch() {
        this.refetchMap.machineName = true;
        this.refetchMap.userName = true;
    }

    private _updateSessionState = (message: ISessionUpdate) => {
        if (!message.isNew) {
            this._grid.dataManager.patchRow(
                message.id,
                message,
            );
            return;
        }

        // reload page for new sessions
        if (
            this._grid.footer!.state.pageIndex === 0 &&
            !this._grid.header!.searchValue &&
            !this._grid.filterManager.filter$.value.length
        ) {
            this._refresh$.next();
        }
    }

    private _updateRobotLicensesState = ({ robotId, hasLicense, timestamp }: IRobotLicenseUpdate) => {
        const session = this._findSessionByRobotId(robotId);

        if (!session || !session.robot) { return; }


        this._grid.dataManager.patchRow(session.id, {
            robot: {
                license: hasLicense ? { timestamp } : undefined,
            },
        });
    }

    private _findSessionByRobotId(robotId: number) {
        return this._grid.dataManager.data$.value.find(entry => entry.robot.id === robotId);
    }
}
